import { DOMAIN } from "../constants";
import { getValidAccessToken } from "../api/authToken";

/**
 * POST a JSON body and save the file that comes back.
 *
 * Exports are POSTs rather than links because the filters that decide what is
 * in the file are too big for a query string — and because these endpoints are
 * JWT-protected, so `window.location.href` cannot reach them: the token lives
 * in localStorage, not a cookie, and a navigation carries no Authorization
 * header.
 */
export async function downloadPost(
  path: string,
  body: unknown,
  fallbackFileName: string
): Promise<void> {
  const token = await getValidAccessToken();
  const response = await fetch(`${DOMAIN}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let detail = "";
    try {
      const parsed: { message?: string; detail?: string } = await response.json();
      detail = parsed?.message ?? parsed?.detail ?? "";
    } catch {
      /* not a JSON error body */
    }
    throw new Error(detail || `Export failed with status ${response.status}`);
  }

  // Prefer the filename the backend suggests.
  let fileName = fallbackFileName;
  const disposition = response.headers.get("Content-Disposition");
  const utf8 = disposition && disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = disposition && disposition.match(/filename="?([^";]+)"?/i);
  if (utf8) {
    fileName = decodeURIComponent(utf8[1].trim());
  } else if (plain) {
    fileName = plain[1].trim();
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
